import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { breakpoints, spacing } from '../../../utils/styleguide';
import PrimaryButton from '../buttons/PrimaryButton';
import Typography from '../text/Typography';

const ModalWrapper = styled.div`
  box-sizing: border-box;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
`;

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  height: 100%;
  width: 100%;
  position: absolute;
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 532px;
  background-color: white;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 5px 22px rgba(0, 0, 0, 0.12), 0 12px 17px rgba(0, 0, 0, 0.14),
    0 7px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalBody = styled.div<{ centerContent?: boolean }>`
  overflow: auto;
  padding: ${spacing[6]}px ${spacing[4]}px;
  ${({ centerContent }) => `margin: ${centerContent ? 'auto' : '0'};`}
  ${({ centerContent }) => `text-align: ${centerContent ? 'center' : 'unset'};`}
  ${breakpoints.desktop} {
    padding: ${spacing[9]}px ${spacing[6]}px;
  }
`;

const CloseButtonWrapper = styled.div`
  padding-top: ${spacing[6]}px;
  text-align: center;
`;

type Props = {
  header?: string | JSX.Element;
  body?: string | JSX.Element;
  close?: string | JSX.Element;
  onClose?: () => void;
  centerContent?: boolean;
  open?: boolean;
  fullHeight?: boolean;
};

const ModernModal = ({
  open,
  header,
  body,
  centerContent,
  onClose,
  fullHeight = false,
  close = 'Close',
}: Props) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) {
    return null;
  }
  return createPortal(
    <ModalWrapper>
      <Overlay onClick={onClose} />
      <ModalContent css={fullHeight ? { height: '100%' } : {}} aria-modal tabIndex={-1}>
        <ModalBody centerContent={centerContent}>
          {typeof header === 'string' ? (
            <Typography variant="smallTitle">{header}</Typography>
          ) : (
            header
          )}
          {typeof body === 'string' ? <Typography useBuiltInMargins>{body}</Typography> : body}
          {onClose && (
            <CloseButtonWrapper>
              <PrimaryButton color="onYellow" label={close} onClick={onClose} size={'small'} />
            </CloseButtonWrapper>
          )}
        </ModalBody>
      </ModalContent>
    </ModalWrapper>,
    document.body,
  );
};

export default ModernModal;
